/**
 * StepIntro — 4-slide onboarding walkthrough (step 1).
 *
 * Left panel: rendered by OnboardingShell with typewriter headline.
 * Right panel: progress bar + one slide at a time explaining each of the 4 assessment sections.
 */
import React, { useState } from 'react';
import OnboardingShell from './OnboardingShell';

// ── Slide data ────────────────────────────────────────────────────────────────

const SLIDES: Array<{
  icon: string; num: number; weight: string | null;
  title: string; sub: string;
  desc?: string | null;
  descBullets?: React.ReactNode[];
  formula?: string;
  extra?: string;
  tip?: boolean;
}> = [
  {
    icon: '🎯', num: 1, weight: '50%',
    title: 'Review Delivery',
    sub: "What was the employee's impact?",
    desc: "I'll provide a structure to review each deliverable of your direct report. You will rate how well each one was executed and capture which role-specific competencies were demonstrated to get there.",
  },
  {
    icon: '🌟', num: 2, weight: '50%',
    title: 'Review Behaviors',
    sub: 'How did the employee live up to our culture?',
    desc: "I'll show you the behaviors expected at your direct report's job level across FINN's five values. You rate how they showed up, grounded in concrete examples.",
  },
  {
    icon: '📊', num: 3, weight: null,
    title: 'Get Performance Rating',
    sub: 'What is the overall performance rating?',
    desc: "Based on your ratings, I'll calculate a suggestion for the overall performance rating:",
    formula: 'Delivery (50%) + Behaviors (50%) = Overall Performance Rating',
    extra: "If there's a tie (e.g. an overall of 3.5), Delivery takes precedence. While this is the company-wide guidance, the overall rating will be in your discretion.",
  },
  {
    icon: '✨', num: 4, weight: null,
    title: 'Get Manager Review',
    sub: 'How do you share the review with your employee?',
    desc: null,
    descBullets: [
      "I'll take everything you've noted in steps 1 and 2 and shape it into a structured, bias-aware manager review.",
      <>Only the overall delivery, behavior, and performance ratings, plus your manager review text, are shared with your employee. <strong>Notes and sub-ratings are for your performance assessment only.</strong></>,
      <>You can make any edits and paste it directly into the Culture Amp Manager Review form, <strong>where it will be shared with the employee after the calibration</strong>.</>,
    ],
    tip: true,
  },
];

// ── Component ─────────────────────────────────────────────────────────────────

interface StepIntroProps {
  onNext: () => void;
  onBack: () => void;
}

export default function StepIntro({ onNext, onBack }: StepIntroProps) {
  const [active, setActive] = useState(0);
  const s = SLIDES[active];
  const isLast = active === SLIDES.length - 1;

  return (
    <OnboardingShell leftTitle="Four steps to complete your manager review.">

      {/* Progress bar */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 40 }}>
        {SLIDES.map((_, i) => (
          <div key={i} style={{
            height: 3, flex: 1, borderRadius: 2,
            background: i <= active ? '#0087eb' : '#e5e7eb',
            transition: 'background 0.35s',
          }} />
        ))}
      </div>

      {/* Card content */}
      <div style={{ minHeight: 280 }}>
        {/* Number + weight badge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
          <div style={{
            width: 36, height: 36, borderRadius: '50%',
            background: '#0087eb', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800, fontSize: 16, flexShrink: 0,
          }}>{s.num}</div>
          {s.weight && (
            <span style={{
              marginLeft: 'auto', background: '#0087eb18',
              color: '#0087eb', borderRadius: 20,
              padding: '4px 14px', fontSize: 12, fontWeight: 700,
            }}>Counts {s.weight}</span>
          )}
        </div>

        {/* Title */}
        <h2 style={{
          fontSize: 26, fontWeight: 800, letterSpacing: '-0.025em',
          marginBottom: 6, color: '#0f0f0f',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{ fontSize: 26, lineHeight: 1 }}>{s.icon}</span>
          {s.title}
        </h2>

        {/* Subtitle */}
        <div style={{
          fontSize: 12, fontWeight: 700, color: '#0087eb',
          textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 18,
        }}>{s.sub}</div>

        {/* Description — plain paragraph or bullet list */}
        {s.desc && (
          <p style={{ fontSize: 15, color: '#555', lineHeight: 1.8, margin: 0 }}>{s.desc}</p>
        )}
        {'descBullets' in s && s.descBullets && (
          <ul style={{ fontSize: 15, color: '#555', lineHeight: 1.8, margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {s.descBullets.map((b, i) => <li key={i}>{b}</li>)}
          </ul>
        )}

        {'formula' in s && s.formula && (
          <div style={{
            margin: '12px 0', padding: '11px 16px',
            background: '#f5f5f5', borderLeft: '3px solid #d1d5db',
            borderRadius: '0 6px 6px 0', fontWeight: 400, fontSize: 14, color: '#444',
          }}>{s.formula}</div>
        )}
        {'extra' in s && s.extra && (
          <p style={{ fontSize: 15, color: '#555', lineHeight: 1.8, margin: 0 }}>{s.extra}</p>
        )}

        {'tip' in s && s.tip && (
          <div style={{
            marginTop: 24, padding: '13px 16px',
            background: 'rgba(232,41,74,0.05)',
            border: '1px solid rgba(232,41,74,0.3)', borderRadius: 8,
          }}>
            <p style={{ fontSize: 13, color: '#1a1a1a', lineHeight: 1.5, margin: '0 0 8px', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <span style={{ flexShrink: 0 }}>❗️</span>
              <strong>The review will only be as good as your input.</strong>
            </p>
            <ul style={{ fontSize: 13, color: '#1a1a1a', lineHeight: 1.7, margin: 0, paddingLeft: 18, listStyleType: 'disc' }}>
              <li>The more context you give me, the less you'll have to edit.</li>
              <li>Share specific observations and examples in steps 1 and 2.</li>
              <li>Brain dump or raw notes are perfect. I'll structure it.</li>
            </ul>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div style={{ display: 'flex', gap: 12, marginTop: 32 }}>
        <button
          onClick={active === 0 ? onBack : () => setActive(a => a - 1)}
          style={{
            fontFamily: 'Inter', fontWeight: 600, fontSize: 14,
            border: '1.5px solid #e5e7eb', cursor: 'pointer',
            borderRadius: 8, padding: '12px 20px',
            background: 'transparent', color: '#1a1a1a',
          }}
        >← Back</button>

        <button
          onClick={isLast ? onNext : () => setActive(a => a + 1)}
          style={{
            fontFamily: 'Inter', fontWeight: 700, fontSize: 15,
            border: 'none', cursor: 'pointer', borderRadius: 8,
            padding: '14px 20px', background: '#0087eb', color: '#fff',
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          {isLast ? "Let's begin →" : "Got it →"}
        </button>
      </div>

      {/* Counter */}
      <div style={{ textAlign: 'center', marginTop: 14, fontSize: 12, color: '#bbb', letterSpacing: '0.04em' }}>
        {active + 1} of {SLIDES.length}
      </div>

    </OnboardingShell>
  );
}
